import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import dayjs, { Dayjs } from "dayjs";
import "./SailRangePicker.css";

export interface SailRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date, end: Date) => void;
  placeholder?: [string, string];
}

const todayAtMidnight = (): Dayjs => dayjs().startOf("day");

const formatDate = (date: Date | null): string =>
  date ? dayjs(date).format("DD/MM/YYYY") : "";

// Next Saturday on or after a given date
const nextSaturdayFrom = (d: Dayjs): Dayjs => {
  const diff = (6 - d.day() + 7) % 7;
  return d.add(diff === 0 ? 0 : diff, "day").startOf("day");
};

const isSaturday = (d: Dayjs) => d.day() === 6;

type PickStep = "start" | "end";

const SailRangePicker: React.FC<SailRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  placeholder = ["Check-in", "Check-out"],
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<PickStep>("start");
  const [tempStart, setTempStart] = useState<Dayjs | null>(null);
  const [navMode, setNavMode] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const pickerViewRef = useRef<string>('day');
  const theme = useTheme();

  const today = todayAtMidnight();
  const firstSelectableSat = nextSaturdayFrom(today);

  const dayjsStart = startDate ? dayjs(startDate) : null;
  const dayjsEnd = endDate ? dayjs(endDate) : null;

  const [leftMonth, setLeftMonth] = useState<Dayjs>(dayjsStart || firstSelectableSat);

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    });
  }, []);

  const handleOpen = () => {
    updatePos();
    setLeftMonth(dayjsStart || firstSelectableSat);
    setStep("start");
    setTempStart(null);
    setNavMode(false);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      const dropdown = document.getElementById("sail-rdp-dropdown");
      if (dropdown?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  // Only Saturdays are enabled
  const shouldDisableDate = (d: Dayjs) => !isSaturday(d);

  // Day cell renderer — highlights range and marks Saturdays green
  const makeDayRenderer = () =>
    (props: PickersDayProps<Dayjs>) => {
      const { day, outsideCurrentMonth, disabled, ...rest } = props;
      const isSat = isSaturday(day);

      const rangeStart = step === "end" && tempStart ? tempStart : dayjsStart;
      const rangeEnd = step === "end" ? null : dayjsEnd;

      const isStart = rangeStart && day.isSame(rangeStart, "day");
      const isEnd = rangeEnd && day.isSame(rangeEnd, "day");
      const inRange =
        rangeStart && rangeEnd &&
        day.isAfter(rangeStart, "day") &&
        day.isBefore(rangeEnd, "day");

      return (
        <PickersDay
          {...rest}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          disabled={disabled}
          sx={{
            // non-Saturday cells: fade out so user clearly sees only Sat is pickable
            ...(!isSat && {
              opacity: 0.70,
              pointerEvents: "none",
            }),
            // Saturday base style (green)
            ...(isSat && !outsideCurrentMonth && !isStart && !isEnd && {
              color: "#00a854",
              fontWeight: "bold",
              backgroundColor: "#f2f2f2",
            }),
            // days inside selected range
            ...(inRange && {
              backgroundColor: "rgba(25, 118, 210, 0.12)",
              borderRadius: 0,
            }),
            // selected start
            ...(isStart && {
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "50% 0 0 50%",
            }),
            // selected end
            ...(isEnd && {
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "0 50% 50% 0",
            }),
          }}
        />
      );
    };

  const handleDayClick = (value: Dayjs | null) => {
    if (!value || !isSaturday(value)) return;
    if (step === "start") {
      setTempStart(value);
      setStep("end");
    } else {
      const start = tempStart!;
      const end = value.isBefore(start) ? start : value;
      // enforce minimum 7-day (1 week) stay
      const minEnd = start.add(7, "day");
      onChange(start.toDate(), (end.isBefore(minEnd) ? minEnd : end).toDate());
      setOpen(false);
      setStep("start");
      setTempStart(null);
    }
  };

  // minDate for end panel: start + 7 days (next Saturday)
  const endMinDate = tempStart
    ? tempStart.add(7, "day")
    : dayjsStart
    ? dayjsStart.add(7, "day")
    : firstSelectableSat.add(7, "day");

  const dropdown = open
    ? createPortal(
        <div
          id="sail-rdp-dropdown"
          style={{
            position: "absolute",
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 9999,
            boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
            borderRadius: "8px",
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* Single calendar — check-in then check-out */}
          <Box>
            <Typography sx={{ px: 2, pt: 1, fontSize: "12px", color: "#888" }}>
              {step === "start"
                ? "Select check-in (Saturdays only)"
                : "Check-in: " + (tempStart?.format("DD/MM/YYYY") ?? "") + " — now select check-out"}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StaticDatePicker
                value={navMode ? null : (step === "end" ? tempStart : dayjsStart)}
                onChange={(value) => {
                  if (pickerViewRef.current === 'month') {
                    if (value) {
                      setLeftMonth(value);
                      setNavMode(true);
                    }
                    return;
                  }
                  setNavMode(false);
                  handleDayClick(value);
                }}
                onViewChange={(view) => { pickerViewRef.current = view; }}
                minDate={step === "end" ? endMinDate : firstSelectableSat}
                shouldDisableDate={shouldDisableDate}
                referenceDate={leftMonth}
                onMonthChange={(m) => setLeftMonth(m)}
                views={["month", "day"]}
                openTo="day"
                slots={{ day: makeDayRenderer() }}
                slotProps={{
                  toolbar: { hidden: true },
                  actionBar: { actions: [] },
                }}
                sx={{ ".MuiPickersCalendarHeader-root": { mt: 0 } }}
              />
            </LocalizationProvider>
          </Box>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <Grid
        ref={triggerRef}
        container
        onClick={handleOpen}
        sx={{ cursor: "pointer", minHeight: "28px", alignItems: "center" }}
      >
        <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ fontSize: "14px", color: startDate ? "#000" : "#aaa" }}>
            {startDate ? formatDate(startDate) : placeholder[0]}
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex", alignItems: "center", pl: 2 }}>
          <Typography sx={{ fontSize: "14px", color: endDate ? "#000" : "#aaa" }}>
            {endDate ? formatDate(endDate) : placeholder[1]}
          </Typography>
        </Grid>
      </Grid>
      {dropdown}
    </>
  );
};

export default SailRangePicker;
